import "rc-tree/assets/index.css";
import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import Tree from "rc-tree";
import styles from "./DataTree.module.css";
import { findAncestors, toHierarchy } from "@maxis/client-logic/utils/dataUtil";

const getAllExpandableKeys = (items, idName) => {
  const keys = [];

  const appendKeys = (nodes) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length) {
        keys.push(node[idName]);
        appendKeys(node.children);
      }
    });
  };

  appendKeys(items);
  return keys;
};

const getKeysToLevel = (items, idName, level) => {
  if (level <= 0) return [];

  const keys = [];
  const appendKeys = (nodes, currLevel) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length && currLevel < level) {
        keys.push(node[idName]);
        appendKeys(node.children, currLevel + 1);
      }
    });
  };

  appendKeys(items, 0);
  return keys;
};

const getItemKey = (item, idName) => item?.[idName];

const findItemByKey = (items, key, idName) => items.find((item) => item[idName] === key);

const findNodePath = (items, item, idName, parentIdName) => {
  if (!item) return [];

  const itemById = new Map(items.map((sourceItem) => [sourceItem[idName], sourceItem]));
  const path = [];
  const visited = new Set();
  let currItem = item;

  while (currItem && !visited.has(currItem[idName])) {
    path.unshift(currItem);
    visited.add(currItem[idName]);
    currItem = itemById.get(currItem[parentIdName]);
  }

  return path;
};

const createSelectedNode = (path) => {
  let parentNode = null;
  let selectedNode = null;

  path.forEach((item) => {
    selectedNode = {
      dataItem: item,
      parentNode,
    };
    parentNode = selectedNode;
  });

  return selectedNode;
};

/**
 * DataTree
 * @type React.FC<DataTreePropTypes>
 */
const DataTree = React.forwardRef((props, ref) => {
  const refTree = useRef();
  const refExpand = useRef(props.expandOnLoaded);
  const stateRef = useRef({
    originSource: [],
    treeData: [],
    selectedItem: null,
    checkedItems: [],
    expandedKeys: [],
  });

  const [currState, setCurrStateBase] = useState(stateRef.current);

  const setCurrState = (updater) => {
    setCurrStateBase((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      stateRef.current = next;
      return next;
    });
  };

  React.useImperativeHandle(ref, () => ({
    setDataBinding(source) {
      const originSource = Array.isArray(source) ? source : [];
      const treeData = toHierarchy(originSource, props.idName, props.parentIdName);
      let checkedItems = [];
      let expandedKeys = [];

      if (props.showCheckboxes) {
        checkedItems = originSource.filter((e) => e[props.checkedMemberPath]);
      }

      if (props.isExpandAll || props.expandOnLoaded || refExpand.current) {
        expandedKeys = getAllExpandableKeys(treeData, props.idName);
      }

      setCurrState((prev) => ({
        ...prev,
        originSource: originSource,
        treeData: treeData,
        checkedItems: checkedItems,
        expandedKeys: expandedKeys,
      }));
    },
    setSelectedItem(id) {
      const { originSource } = stateRef.current;
      const found = originSource.find((e) => e[props.idName] === id);
      if (found) {
        setCurrState((prev) => ({
          ...prev,
          selectedItem: found,
          expandedKeys: [
            ...new Set([
              ...prev.expandedKeys,
              ...findNodePath(prev.originSource, found, props.idName, props.parentIdName)
                .slice(0, -1)
                .map((item) => item[props.idName]),
            ]),
          ],
        }));
      }
    },
    getSelectedItem() {
      return stateRef.current.selectedItem;
    },
    getCheckedItems(includePaths = true) {
      // Tree 상 Check 된 Item의 상위 Node들도 가져오기
      const { checkedItems, originSource } = stateRef.current;
      if (includePaths) {
        const checkedIds = checkedItems.map((item) => item[props.idName]);
        return findAncestors(originSource, checkedIds, props.idName, props.parentIdName);
      }
      return checkedItems;
    },

    setDefaultSelectedItem(selectItem = null) {
      if (selectItem) {
        setCurrState((prev) => ({
          ...prev,
          selectedItem: selectItem,
          expandedKeys: [
            ...new Set([
              ...prev.expandedKeys,
              ...findNodePath(prev.originSource, selectItem, props.idName, props.parentIdName)
                .slice(0, -1)
                .map((item) => item[props.idName]),
            ]),
          ],
        }));
      }
    },

    changeExpanded(changeValue = null) {
      if (changeValue != null) {
        refExpand.current = true;
        setCurrState((prev) => ({
          ...prev,
          expandedKeys: getAllExpandableKeys(prev.treeData, props.idName),
        }));
      }
    },
  }));

  /**
   * @author 이동현
   */
  const _initialized = (ctrl) => {
    refTree.current = ctrl;
  };

  /**
   * @author 이동현
   */
  React.useEffect(() => {
    const ctrl = {
      collapseToLevel(level) {
        setCurrState((prev) => ({
          ...prev,
          expandedKeys: getKeysToLevel(prev.treeData, props.idName, level),
        }));
      },
      get selectedItem() {
        return stateRef.current.selectedItem;
      },
      get checkedItems() {
        return stateRef.current.checkedItems;
      },
    };

    if (props.loadedItems) {
      props.loadedItems(ctrl);
    }
  }, [currState.treeData]);

  const _selectedItemChanged = (selectedKeys, info) => {
    const selectedKey = selectedKeys[0] ?? getItemKey(info.node, props.idName);
    const selectedItem =
      findItemByKey(currState.originSource, selectedKey, props.idName) || info.node;
    const selectedNodePath = findNodePath(
      currState.originSource,
      selectedItem,
      props.idName,
      props.parentIdName
    );
    const selectedNode = createSelectedNode(selectedNodePath);
    const event = {
      selectedItem,
      selectedNode,
      selectedItems: selectedItem ? [selectedItem] : [],
      nativeEvent: info.nativeEvent,
    };

    setCurrState((prev) => ({
      ...prev,
      selectedItem: selectedItem,
    }));

    if (props.onSelectedItemChanged) {
      props.onSelectedItemChanged(event, selectedNodePath);
    }
  };

  const _expandedItemsChanged = (expandedKeys) => {
    setCurrState((prev) => ({
      ...prev,
      expandedKeys,
    }));
  };

  const _onCheckedItemsChanged = (checkedKeys, info) => {
    const nextCheckedKeys = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
    const checkedKeySet = new Set(nextCheckedKeys);
    const checkedItems = currState.originSource.filter((item) =>
      checkedKeySet.has(item[props.idName])
    );

    currState.originSource.forEach((item) => {
      item[props.checkedMemberPath] = checkedKeySet.has(item[props.idName]);
    });

    const event = {
      checkedItems,
      checkedItem: findItemByKey(
        currState.originSource,
        getItemKey(info.node, props.idName),
        props.idName
      ),
      checked: info.checked,
      node: info.node,
      nativeEvent: info.nativeEvent,
    };

    setCurrState((prev) => ({
      ...prev,
      checkedItems,
    }));

    if (props.checkedItemsChanged) {
      props.checkedItemsChanged(event);
    }
  };

  return (
    <div className={styles.container}>
      <Tree
        ref={_initialized}
        className={styles.treeView}
        treeData={currState.treeData}
        fieldNames={{
          title: props.displayMemberPath,
          key: props.idName,
          children: props.childItemsPath,
        }}
        selectable={true}
        selectedKeys={
          currState.selectedItem ? [getItemKey(currState.selectedItem, props.idName)] : []
        }
        checkable={props.showCheckboxes}
        checkedKeys={currState.checkedItems.map((item) => item[props.idName])}
        expandedKeys={currState.expandedKeys}
        onSelect={_selectedItemChanged}
        onCheck={_onCheckedItemsChanged}
        onExpand={_expandedItemsChanged}
        virtual={false}
      />
    </div>
  );
});

const DataTreePropTypes = {
  /**
   * 화면에 출력되는 Text 항목
   */
  displayMemberPath: PropTypes.string,
  childItemsPath: PropTypes.string,
  idName: PropTypes.string,
  parentIdName: PropTypes.string,
  onSelectedItemChanged: PropTypes.func,
  checkedItemsChanged: PropTypes.func,
  loadedItems: PropTypes.func,
  showCheckboxes: PropTypes.bool,
  checkedMemberPath: PropTypes.string,
  expandOnLoaded: PropTypes.bool,
  /**
   * @author 이동현
   */
  isExpandAll: PropTypes.bool,
};

DataTree.propTypes = DataTreePropTypes;
DataTree.defaultProps = {
  displayMemberPath: "name",
  childItemsPath: "children",
  idName: "id",
  parentIdName: "parentId",
  onSelectedItemChanged: null,
  checkedItemsChanged: null,
  loadedItems: null,
  showCheckboxes: false,
  checkedMemberPath: "chk",
  expandOnLoaded: false,
  isExpandAll: true,
};
DataTree.displayName = "DataTree";
export default DataTree;
